import React, { useState, useRef, useEffect } from 'react';
import { authAPI, setTokens } from '../api/client';
import { Mail, User, Loader2, ArrowRight, ArrowLeft } from 'lucide-react';
import { useTokens } from '../context/ThemeContext';

const LOCALE_CURRENCY_MAP = {
  IN: 'INR', US: 'USD', GB: 'GBP', EU: 'EUR',
  DE: 'EUR', FR: 'EUR', IT: 'EUR', ES: 'EUR', NL: 'EUR', PT: 'EUR',
  BE: 'EUR', AT: 'EUR', FI: 'EUR', IE: 'EUR', GR: 'EUR', LU: 'EUR',
  JP: 'JPY', AU: 'AUD', CA: 'CAD', CH: 'CHF', SG: 'SGD',
  AE: 'AED', SA: 'SAR', MY: 'MYR', TH: 'THB', ID: 'IDR',
  PH: 'PHP', KR: 'KRW', CN: 'CNY', HK: 'HKD', VN: 'VND',
  TW: 'TWD', NZ: 'NZD', MX: 'MXN', BR: 'BRL', ZA: 'ZAR',
  NO: 'NOK', SE: 'SEK', DK: 'DKK', NG: 'NGN', KE: 'KES',
  EG: 'EGP', PK: 'PKR', BD: 'BDT',
};

function detectCurrency() {
  try {
    const fmt = new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' });
    const locale = fmt.resolvedOptions().locale;
    const region = locale.split('-').pop().toUpperCase();
    return LOCALE_CURRENCY_MAP[region] || 'USD';
  } catch {
    return 'USD';
  }
}

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
    <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4"/>
    <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
    <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
    <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
  </svg>
);

function OTPInput({ value, onChange, onKeyDown, inputRef, autoFocus }) {
  return (
    <input
      ref={inputRef}
      type="text"
      inputMode="numeric"
      maxLength={1}
      value={value}
      autoFocus={autoFocus}
      onChange={onChange}
      onKeyDown={onKeyDown}
      style={{
        width: '44px',
        height: '52px',
        textAlign: 'center',
        fontSize: '22px',
        fontWeight: '700',
        fontFamily: 'monospace',
        border: '1.5px solid var(--otp-border, #d1d5db)',
        borderRadius: '10px',
        outline: 'none',
        backgroundColor: 'var(--otp-bg, #f9fafb)',
        color: 'var(--otp-text, #1e293b)',
        transition: 'border-color 0.15s',
      }}
      onFocus={(e) => e.target.select()}
    />
  );
}

function Login({ onLoginSuccess, initialMode = 'login' }) {
  const t = useTokens();
  const [mode, setMode] = useState(initialMode); // 'login' | 'signup'
  const [step, setStep] = useState('email'); // 'email' | 'code'
  const [email, setEmail] = useState('');
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  const digitRefs = useRef([]);

  // Countdown for resend button
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    setError('');
    try {
      sessionStorage.setItem('detectedCurrency', detectCurrency());
      const response = await authAPI.getGoogleLoginUrl();
      window.location.href = response.data.authUrl;
    } catch {
      setError('Failed to initiate Google sign-in');
      setGoogleLoading(false);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setError('');
    setLoading(true);
    try {
      await authAPI.requestLoginOTP(email.trim());
      setDigits(['', '', '', '', '', '']);
      setStep('code');
      setResendCooldown(60);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send code');
    } finally {
      setLoading(false);
    }
  };

  const handleDigitChange = (index, raw) => {
    const char = raw.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[index] = char;
    setDigits(next);
    setError('');
    if (char && index < 5) {
      digitRefs.current[index + 1]?.focus();
    }
    // Auto-submit when all 6 digits filled
    if (char && index === 5 && next.every((d) => d)) {
      submitCode(next.join(''));
    }
  };

  const handleDigitKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      digitRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && index > 0) digitRefs.current[index - 1]?.focus();
    if (e.key === 'ArrowRight' && index < 5) digitRefs.current[index + 1]?.focus();
  };

  const handleCodePaste = (e) => {
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!text) return;
    e.preventDefault();
    const next = [...digits];
    for (let i = 0; i < 6; i++) next[i] = text[i] || '';
    setDigits(next);
    const lastFilled = Math.min(text.length - 1, 5);
    digitRefs.current[lastFilled]?.focus();
    if (text.length === 6) submitCode(text);
  };

  const submitCode = async (code) => {
    setError('');
    setLoading(true);
    try {
      const response = await authAPI.verifyLoginOTP(email.trim(), code, null, detectCurrency());
      const { accessToken, refreshToken } = response.data;
      setTokens(accessToken, refreshToken);
      if (onLoginSuccess) {
        onLoginSuccess(response.data.user);
      } else {
        window.location.reload();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid code');
      setDigits(['', '', '', '', '', '']);
      digitRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSubmit = (e) => {
    e.preventDefault();
    const code = digits.join('');
    if (code.length < 6) {
      setError('Enter the 6-digit code');
      return;
    }
    submitCode(code);
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setError('');
    setLoading(true);
    try {
      await authAPI.requestLoginOTP(email.trim());
      setDigits(['', '', '', '', '', '']);
      setResendCooldown(60);
      digitRefs.current[0]?.focus();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  const cardStyle = {
    backgroundColor: t.surface,
    padding: '40px',
    borderRadius: '16px',
    boxShadow: t.cardShadow,
    textAlign: 'center',
    width: '100%',
    maxWidth: '400px',
  };

  const isSignup = mode === 'signup';

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: t.bgGradient, padding: '20px' }}>
      <div style={cardStyle}>
        <h1 style={{ fontSize: '48px', fontWeight: '600', margin: '0 0 6px 0', color: t.textSlate, letterSpacing: '0.02em' }}>nura</h1>
        <p style={{ fontSize: '16px', color: t.textSecondary, margin: '0 0 28px 0' }}>Mindful spending, one day at a time</p>

        {/* Login / Sign up tabs */}
        {step === 'email' && (
          <div style={{ display: 'flex', backgroundColor: t.chipBg, borderRadius: '10px', padding: '4px', marginBottom: '24px' }}>
            <button
              onClick={() => { setMode('login'); setError(''); }}
              style={{ flex: 1, padding: '8px', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer', backgroundColor: !isSignup ? t.surface : 'transparent', color: !isSignup ? t.text : t.textMuted, boxShadow: !isSignup ? '0 1px 3px rgba(0,0,0,0.08)' : 'none', transition: 'all 0.15s' }}
            >
              Log in
            </button>
            <button
              onClick={() => { setMode('signup'); setError(''); }}
              style={{ flex: 1, padding: '8px', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer', backgroundColor: isSignup ? t.surface : 'transparent', color: isSignup ? t.text : t.textMuted, boxShadow: isSignup ? '0 1px 3px rgba(0,0,0,0.08)' : 'none', transition: 'all 0.15s' }}
            >
              Sign up
            </button>
          </div>
        )}

        {step === 'email' ? (
          <>
            {isSignup && (
              <p style={{ fontSize: '13px', color: t.textSecondary, margin: '-8px 0 16px 0', textAlign: 'left' }}>
                Create your free account — no password needed.
              </p>
            )}

            <form onSubmit={handleEmailSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <Mail size={18} color={t.textMuted} style={{ position: 'absolute', left: '14px', pointerEvents: 'none' }} />
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setError(''); }}
                  required
                  autoFocus
                  style={{ width: '100%', padding: '14px 14px 14px 44px', border: `1px solid ${t.inputBorder}`, borderRadius: '10px', fontSize: '15px', outline: 'none', boxSizing: 'border-box', backgroundColor: t.inputBg, color: t.text }}
                />
              </div>

              {error && <p style={{ color: t.danger, fontSize: '13px', margin: 0, textAlign: 'left' }}>{error}</p>}

              <button type="submit" disabled={loading} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', backgroundColor: t.textSlate, color: 'white', border: 'none', padding: '14px 24px', fontSize: '16px', borderRadius: '12px', cursor: loading ? 'not-allowed' : 'pointer', width: '100%', fontWeight: '500', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', marginTop: '8px', opacity: loading ? 0.7 : 1 }}>
                {loading ? (
                  <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
                ) : (
                  <>
                    {isSignup ? 'Create account' : 'Continue'}
                    <ArrowRight size={18} />
                  </>
                )}
              </button>
            </form>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '20px 0 0 0' }}>
              <span style={{ flex: 1, height: '1px', backgroundColor: t.border }} />
              <span style={{ fontSize: '13px', color: t.textMuted }}>or</span>
              <span style={{ flex: 1, height: '1px', backgroundColor: t.border }} />
            </div>

            <button
              onClick={handleGoogleLogin}
              disabled={googleLoading}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', backgroundColor: t.surface, color: t.textSlate, border: `1px solid ${t.border}`, padding: '12px 24px', fontSize: '15px', borderRadius: '12px', cursor: 'pointer', width: '100%', fontWeight: '500', marginTop: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}
            >
              {googleLoading ? <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> : <GoogleIcon />}
              {isSignup ? 'Sign up with Google' : 'Continue with Google'}
            </button>

            <p style={{ fontSize: '13px', color: t.textMuted, marginTop: '20px' }}>
              {isSignup ? (
                <>Already have an account?{' '}
                  <button onClick={() => { setMode('login'); setError(''); }} style={{ background: 'none', border: 'none', color: t.textSlate, fontWeight: '500', fontSize: '13px', cursor: 'pointer', padding: 0 }}>Log in</button>
                </>
              ) : (
                <>New here?{' '}
                  <button onClick={() => { setMode('signup'); setError(''); }} style={{ background: 'none', border: 'none', color: t.textSlate, fontWeight: '500', fontSize: '13px', cursor: 'pointer', padding: 0 }}>Create a free account</button>
                </>
              )}
            </p>
          </>
        ) : (
          <>
            <p style={{ fontSize: '14px', color: t.textSecondary, margin: '0 0 8px 0' }}>
              {isSignup ? 'One last step — enter the code sent to' : 'We sent a 6-digit code to'}
            </p>
            <p style={{ fontSize: '15px', fontWeight: '600', color: t.text, margin: '0 0 28px 0' }}>{email}</p>

            <form onSubmit={handleCodeSubmit} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' }}>
              <div style={{ display: 'flex', gap: '8px' }} onPaste={handleCodePaste}>
                {digits.map((d, i) => (
                  <OTPInput
                    key={i}
                    value={d}
                    autoFocus={i === 0}
                    inputRef={(el) => { digitRefs.current[i] = el; }}
                    onChange={(e) => handleDigitChange(i, e.target.value)}
                    onKeyDown={(e) => handleDigitKeyDown(i, e)}
                  />
                ))}
              </div>

              {error && <p style={{ color: t.danger, fontSize: '13px', margin: 0 }}>{error}</p>}

              <button type="submit" disabled={loading || digits.join('').length < 6} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', backgroundColor: t.textSlate, color: 'white', border: 'none', padding: '14px 24px', fontSize: '16px', borderRadius: '12px', cursor: (loading || digits.join('').length < 6) ? 'not-allowed' : 'pointer', width: '100%', fontWeight: '500', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', opacity: (loading || digits.join('').length < 6) ? 0.6 : 1 }}>
                {loading ? (
                  <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
                ) : (
                  <>
                    {isSignup ? 'Create account' : 'Sign in'}
                    <ArrowRight size={18} />
                  </>
                )}
              </button>
            </form>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px' }}>
              <button
                onClick={() => { setStep('email'); setError(''); setDigits(['', '', '', '', '', '']); }}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', color: t.textSecondary, fontSize: '13px', cursor: 'pointer', padding: '4px 0' }}
              >
                <ArrowLeft size={14} />
                Change email
              </button>

              <button
                onClick={handleResend}
                disabled={resendCooldown > 0 || loading}
                style={{ background: 'none', border: 'none', color: resendCooldown > 0 ? t.textMuted : t.textSlate, fontSize: '13px', cursor: resendCooldown > 0 ? 'default' : 'pointer', padding: '4px 0', fontWeight: '500' }}
              >
                {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
              </button>
            </div>
          </>
        )}

        <p style={{ fontSize: '12px', color: t.textMuted, marginTop: '24px', lineHeight: '1.5' }}>
          Track your expenses by uploading bank statements or connecting Gmail for automatic sync.
        </p>
      </div>
    </div>
  );
}

export default Login;
